import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type { NotificationDto } from '@/types/notification';

const PAGE_SIZE = 20;

// ── Query key factory ──────────────────────────────────────────────────────
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (opts: { unreadOnly?: boolean; limit?: number }) =>
    [...notificationKeys.all, 'list', opts] as const,
  infinite: (opts: { unreadOnly?: boolean; limit?: number }) =>
    [...notificationKeys.all, 'infinite', opts] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────

export function useNotifications(opts: { unreadOnly?: boolean; limit?: number } = {}) {
  return useQuery({
    queryKey: notificationKeys.list(opts),
    queryFn: async () => {
      const { data } = await apiClient.get<NotificationDto[]>('/notifications', {
        params: {
          unreadOnly: opts.unreadOnly ?? false,
          limit: opts.limit ?? 20,
        },
      });
      return data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Same underlying list, paged via `offset` for the bell dropdown's infinite
 * scroll. A page shorter than the requested limit means there's nothing more
 * to fetch — no separate total-count query needed.
 */
export function useInfiniteNotifications(opts: { unreadOnly?: boolean; limit?: number } = {}) {
  const limit = opts.limit ?? PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: notificationKeys.infinite(opts),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<NotificationDto[]>('/notifications', {
        params: {
          unreadOnly: opts.unreadOnly ?? false,
          limit,
          offset: pageParam,
        },
      });
      return data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < limit ? undefined : allPages.reduce((sum, page) => sum + page.length, 0),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<NotificationDto>(
        `/notifications/${id}/read`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ count: number }>(
        '/notifications/read-all',
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
